import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';

const WellnessCenterCard = ({ center, onSelect, isSelected = false }) => {
  const [isFavorited, setIsFavorited] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const mockImages = [
    "/assets/images/center-1.jpg",
    "/assets/images/center-2.jpg",
    "/assets/images/center-3.jpg"
  ];

  const toggleFavorite = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsFavorited(!isFavorited);
    // Animate heart
    const animation = e.currentTarget.animate([
      { transform: 'scale(1)' },
      { transform: 'scale(1.2)' },
      { transform: 'scale(1)' }
    ], {
      duration: 200,
      easing: 'ease-out'
    });
  };

  const handleImageNavigation = (direction, e) => {
    e.preventDefault();
    e.stopPropagation();

    if (direction === 'next') {
      setCurrentImageIndex((prev) => (prev + 1) % mockImages.length);
    } else {
      setCurrentImageIndex((prev) => (prev - 1 + mockImages.length) % mockImages.length);
    }
  };

  const getAvailabilityColor = (availability) => {
    switch (availability) {
      case 'available':
        return 'text-success bg-success-100 border-success-100'; // Updated from green
      case 'limited':
        return 'text-warning bg-warning-100 border-warning-100'; // Updated from yellow
      case 'booking-required':
        return 'text-primary bg-background-light border-border'; // Updated from blue
      default:
        return 'text-text-secondary bg-background-light border-border'; // Updated from gray
    }
  };

  const getAvailabilityText = (availability) => {
    switch (availability) {
      case 'available':
        return 'Available Now';
      case 'limited':
        return 'Limited Spots';
      case 'booking-required':
        return 'Booking Required';
      default:
        return 'Check Availability';
    }
  };

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className={`group bg-white rounded-2xl shadow-elevation-2 hover:shadow-elevation-4
                   transition-all duration-300 border overflow-hidden ${
        isSelected ? 'border-primary ring-2 ring-background-medium' : 'border-border' // Updated from blue
      }`}
      onClick={() => onSelect?.(center)}
    >
      <div className="flex flex-col md:flex-row">
        {/* Image Gallery */}
        <div className="relative w-full md:w-80 h-64 md:h-72 overflow-hidden">
          <div
            className="w-full h-full bg-gradient-to-br from-background-light to-background-medium
                       flex items-center justify-center transition-all duration-300
                       group-hover:scale-105"
          >
            <Icon name="Image" size={48} className="text-text-secondary" /> {/* Updated from blue-300 */}
          </div>

          {/* Image Navigation */}
          <button
            onClick={(e) => handleImageNavigation('prev', e)}
            className="absolute left-2 top-1/2 transform -translate-y-1/2 w-8 h-8 bg-black/50
                       text-white rounded-full flex items-center justify-center opacity-0
                       group-hover:opacity-100 transition-all duration-200 hover:bg-black/70"
          >
            <Icon name="ChevronLeft" size={16} />
          </button>
          <button
            onClick={(e) => handleImageNavigation('next', e)}
            className="absolute right-2 top-1/2 transform -translate-y-1/2 w-8 h-8 bg-black/50
                       text-white rounded-full flex items-center justify-center opacity-0
                       group-hover:opacity-100 transition-all duration-200 hover:bg-black/70"
          >
            <Icon name="ChevronRight" size={16} />
          </button>

          {/* Image Indicators */}
          <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 flex space-x-1">
            {mockImages.map((_, index) => (
              <div
                key={index}
                className={`w-2 h-2 rounded-full transition-all duration-200 ${
                  index === currentImageIndex ? 'bg-white' : 'bg-white/50'
                }`}
              />
            ))}
          </div>

          {/* Favorite Button */}
          <button
            onClick={toggleFavorite}
            className="absolute top-3 right-3 w-10 h-10 bg-white/90 backdrop-blur-sm
                       rounded-full flex items-center justify-center shadow-elevation-2
                       hover:bg-white transition-all duration-200 micro-interaction"
          >
            <Icon
              name="Heart"
              size={18}
              className={`transition-colors duration-200 ${
                isFavorited
                  ? 'text-error fill-current' // Using 'error' color for filled heart
                  : 'text-text-secondary hover:text-error-500' // Using a lighter 'error' shade for hover
              }`}
            />
          </button>

          {/* Type Badge */}
          <div className="absolute top-3 left-3">
            <span className="px-3 py-1 bg-primary text-white text-xs font-medium {/* Updated from blue-600 */}
                             rounded-full shadow-elevation-1">
              {center.type}
            </span>
          </div>

          {/* Featured Badge */}
          {center.featured && (
            <div className="absolute top-12 left-3">
              <span className="px-2 py-1 bg-warning text-warning-900 text-xs font-bold {/* Updated from yellow-400 and yellow-900 */}
                               rounded-md shadow-elevation-1">
                FEATURED
              </span>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 p-6">
          <div className="h-full flex flex-col">
            {/* Header */}
            <div className="mb-4">
              <div className="flex items-start justify-between mb-2">
                <Link
                  to={`/wellness-center-details/${center.id}`}
                  className="group/link"
                  onClick={(e) => e.stopPropagation()}
                >
                  <h3 className="text-xl font-bold text-text-primary font-heading group-hover/link:text-primary transition-colors duration-200"> {/* Updated from blue-600 */}
                    {center.name}
                  </h3>
                </Link>
                <div className={`px-2 py-1 text-xs font-medium rounded-md border ${
                  getAvailabilityColor(center.availability)
                }`}>
                  {getAvailabilityText(center.availability)}
                </div>
              </div>

              <div className="flex items-center text-text-secondary text-sm mb-2">
                <Icon name="MapPin" size={14} className="mr-1" />
                {center.location}
              </div>

              <div className="flex items-center space-x-4 text-sm">
                <div className="flex items-center space-x-1">
                  <Icon name="Star" size={14} className="text-yellow-400 fill-current" />
                  <span className="font-semibold text-text-primary">{center.rating}</span>
                  <span className="text-text-secondary">({center.reviewCount} reviews)</span>
                </div>
                <div className="text-text-primary">
                  <span className="font-bold text-lg">${center.priceFrom}</span>
                  <span className="text-text-secondary text-sm"> /night</span>
                </div>
              </div>
            </div>

            {/* Description */}
            <p className="text-sm text-text-secondary mb-4 line-clamp-2 flex-grow">
              {center.description}
            </p>

            {/* Practitioner */}
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-8 h-8 bg-gradient-to-r from-primary to-accent {/* Updated from blue-400 to teal-400 */}
                               rounded-full flex items-center justify-center">
                <span className="text-xs text-white font-bold">
                  {center.practitioner?.split(' ')[0]?.[0] || 'P'}
                </span>
              </div>
              <div className="text-sm">
                <span className="text-text-secondary">Led by </span>
                <span className="text-text-primary font-semibold">{center.practitioner}</span>
              </div>
            </div>

            {/* Programs/Categories */}
            <div className="mb-4">
              <div className="flex flex-wrap gap-2">
                {center.programs?.slice(0, 3)?.map((program, index) => (
                  <span
                    key={index}
                    className="px-3 py-1 bg-background-light text-primary text-xs font-medium rounded-full" // Updated from blue-50 and blue-700
                  >
                    {program}
                  </span>
                ))}
                {center.programs?.length > 3 && (
                  <span className="px-3 py-1 text-text-secondary text-xs">
                    +{center.programs.length - 3} more
                  </span>
                )}
              </div>
            </div>

            {/* Amenities */}
            <div className="mb-6">
              <div className="flex flex-wrap gap-2">
                {center.amenities?.slice(0, 4)?.map((amenity, index) => (
                  <div key={index} className="flex items-center space-x-1 text-xs text-text-secondary">
                    <Icon name="Check" size={12} className="text-success" /> {/* Updated from green-500 */}
                    <span>{amenity}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex space-x-3 mt-auto">
              <Link
                to={`/wellness-center-details/${center.id}`}
                onClick={(e) => e.stopPropagation()}
                className="flex-1 text-center px-4 py-3 bg-gradient-to-r from-primary
                           to-secondary text-white font-semibold rounded-lg hover:from-secondary
                           hover:to-primary transition-all duration-200 micro-interaction"
              >
                View Details
              </Link>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  // Handle contact action
                }}
                className="px-4 py-3 border-2 border-primary text-primary font-semibold rounded-lg hover:bg-background-light transition-all duration-200 micro-interaction" // Updated from blue
              >
                <Icon name="MessageCircle" size={16} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default WellnessCenterCard;
